import { Fragment, useState } from 'react';
import { flexRender } from '@tanstack/react-table';
import { SourcingFunnelDetails } from 'api-clients/monolith/models/SourcingFunnelDetails';
import { SourcingMetricsFilterRange } from 'api-clients/monolith';
import clsx from 'clsx';
import _range from 'lodash/range';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/utils/classnames';
import { useDataTable } from '@/hooks/use-data-table';

import { useSourcingOpeningsColumns } from './columns';
import { SourcingOpenings } from './sourcing-openings';

interface AggregateViewProps {
  aggregateData: SourcingFunnelDetails[];
  sourcingOpenings: SourcingFunnelDetails[];
  defaultMetricsPeriod?: SourcingMetricsFilterRange | null;
}

export function AggregateView({
  aggregateData,
  sourcingOpenings,
  defaultMetricsPeriod,
}: AggregateViewProps) {
  const intl = useIntl();
  const columns = useSourcingOpeningsColumns(defaultMetricsPeriod);

  const [isExpanded, setIsExpanded] = useState(false);

  const { table: aggregateDataTable } = useDataTable<SourcingFunnelDetails>({
    columns,
    data: aggregateData,
    rowCount: 1,
  });

  const { table: sourcingOpeningsTable } = useDataTable<SourcingFunnelDetails>({
    columns,
    data: sourcingOpenings,
    rowCount: sourcingOpenings.length,
  });

  return (
    <div className="flex flex-col overflow-clip rounded-lg">
      {aggregateDataTable.getRowModel().rows.map(row => {
        const cells = row.getVisibleCells();
        const nameCell = cells.find(c => c.column.id === 'name');
        const metricCells = cells.filter(c => c.column.id !== 'name');

        return (
          <div
            key={row.id}
            className={cn(
              'border-fx-neutral-200 hover:bg-fx-neutral-50 relative z-10 flex items-center overflow-clip rounded-lg border bg-white px-4 py-3 shadow-sm transition-colors',
              isExpanded && '-mb-2',
            )}
          >
            {/* Info column: title + N openings expand button */}
            {nameCell && (
              <div className="flex w-[216px] shrink-0 flex-col items-start justify-center gap-2">
                {flexRender(
                  nameCell.column.columnDef.cell,
                  nameCell.getContext(),
                )}
                <Button
                  variant="link"
                  size="sm"
                  className="hover:no-underline"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {intl.formatMessage(
                    {
                      defaultMessage: '{count} Openings',
                      description:
                        'Label showing the number of job openings in this group where {count} is the number of openings',
                    },
                    { count: sourcingOpenings.length },
                  )}
                  {isExpanded ? <ChevronUp /> : <ChevronDown />}
                </Button>
              </div>
            )}

            {/* Metric columns with dividers */}
            {metricCells.map(cell => (
              <Fragment key={cell.id}>
                <div className="bg-fx-neutral-200 h-20 w-px shrink-0" />
                <div className="flex min-w-[152px] flex-1 flex-col items-center gap-2">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              </Fragment>
            ))}
          </div>
        );
      })}

      {/* Individual opening tiles, slide under the aggregate card */}
      {isExpanded && <SourcingOpenings table={sourcingOpeningsTable} />}
    </div>
  );
}

AggregateView.Skeleton = function AggregateViewSkeleton() {
  return (
    <div className="flex flex-col overflow-clip rounded-lg">
      <div className="border-fx-neutral-200 flex items-center overflow-clip rounded-lg border bg-white px-4 py-3 shadow-sm">
        {/* Info column */}
        <div className="flex w-[216px] shrink-0 flex-col justify-center gap-2 py-1">
          <Skeleton className="h-6 w-36" />
          <Skeleton className="h-5 w-24" />
        </div>
        {/* Metric columns */}
        {_range(6).map(i => (
          <Fragment key={i}>
            <div className="bg-fx-neutral-200 h-20 w-px shrink-0" />
            <div className="flex min-w-[80px] flex-1 flex-col items-center gap-2 py-1">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-6 w-12" />
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
};
